import { WaqLink } from "../../../ui/waq/WaqLink";
import { useRef, useState } from "react";
import { GlowBlob } from "../../../ui/waq/GlowBlob";
import { models, modelsSection, type EngagementModel } from "../../../../content/waq/models";

/**
 * design/Homepage.dc.html lines 130-217.
 *
 * The reference switcher is three bare buttons toggling `display:none`. This is
 * the ARIA tabs pattern instead: roving tabindex, arrow/Home/End traversal, and
 * aria-selected/aria-controls wiring.
 *
 * Every panel is rendered and hidden with `display:none` rather than unmounted,
 * so all three panels' copy is in the served HTML for crawlers.
 */
export function EngagementModels() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (i: number) => {
    setActive(i);
    tabRefs.current[i]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = models.length - 1;
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        select(active === last ? 0 : active + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        select(active === 0 ? last : active - 1);
        break;
      case "Home":
        e.preventDefault();
        select(0);
        break;
      case "End":
        e.preventDefault();
        select(last);
        break;
    }
  };

  return (
    <section className="relative" aria-labelledby="engagement-models-heading">
      <GlowBlob
        position={{ top: -220, right: -160 }}
        size={720}
        alpha={0.3}
        falloff="64%"
        animated
        duration="slow"
      />

      <div className="relative mx-auto max-w-container px-section-x py-section-y">
        <div className="mb-section-head flex flex-col items-center gap-5 text-center motion-safe:animate-rise">
          <p className="text-eyebrow font-extrabold uppercase text-yellow">
            {modelsSection.eyebrow}
          </p>
          <h2
            id="engagement-models-heading"
            className="max-w-[760px] text-h2 font-extrabold"
          >
            {modelsSection.heading}
          </h2>

          <div
            role="tablist"
            aria-label={modelsSection.eyebrow}
            onKeyDown={onKeyDown}
            className="inline-flex flex-wrap justify-center gap-1 rounded-pill border border-ondark-border bg-white/[.08] p-[5px]"
          >
            {models.map((model, i) => (
              <button
                key={model.tab}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`model-tab-${i}`}
                aria-selected={active === i}
                aria-controls={`model-panel-${i}`}
                tabIndex={active === i ? 0 : -1}
                onClick={() => setActive(i)}
                className={`cursor-pointer rounded-pill px-5 py-3 text-seg font-bold transition-colors duration-[250ms] ${
                  active === i ? "bg-yellow text-navy" : "bg-transparent text-white/72"
                }`}
              >
                {model.tab}
              </button>
            ))}
          </div>
        </div>

        <div className="motion-safe:animate-rise">
          {models.map((model, i) => (
            <ModelPanel key={model.number} model={model} index={i} active={active === i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ModelPanel({
  model,
  index,
  active,
}: {
  model: EngagementModel;
  index: number;
  active: boolean;
}) {
  return (
    <div
      role="tabpanel"
      id={`model-panel-${index}`}
      aria-labelledby={`model-tab-${index}`}
      tabIndex={0}
      className={active ? "motion-safe:animate-rise-fast" : "hidden"}
    >
      <div className="grid grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] items-start gap-panel">
        <div>
          <div className="mb-[18px] flex items-center gap-3.5">
            <span
              aria-hidden="true"
              className="text-ghost-num font-black tabular-nums text-ondark-num"
            >
              {model.number}
            </span>
            <span className="rounded-pill border border-white/20 bg-white/10 px-3.5 py-[7px] text-status font-extrabold uppercase text-yellow">
              {model.badge}
            </span>
          </div>

          <h3 className="mb-3.5 text-model-title font-extrabold">{model.title}</h3>

          <p className="mb-7 max-w-[520px] text-body-lg text-ondark-body-alt">
            {model.body}
          </p>

          {/* A problem figure does not get the yellow numeral. Model 01's stat
              is a failure rate, and in the treatment reserved for capabilities
              it reads as a boast about failure — the same correction made on
              /services/ai-driven-validation, kept consistent here. */}
          <p
            className={`mb-[30px] flex items-baseline gap-4 border-t pt-6 ${
              model.statTone === "problem"
                ? "border-l-[5px] border-l-blue border-t-ondark-border pl-5"
                : "border-ondark-border"
            }`}
          >
            <span
              className={`text-stat-sm font-extrabold tabular-nums ${
                model.statTone === "problem" ? "text-surface" : "text-yellow"
              }`}
            >
              {model.stat.value}
            </span>
            <span className="max-w-[240px] text-stat-caption font-bold">
              {model.stat.caption}
            </span>
          </p>

          <WaqLink
            href={model.cta.href}
            className="inline-flex items-center gap-2.5 rounded-btn bg-yellow px-6 py-[15px] text-[15px] font-bold text-navy"
          >
            {model.cta.label}{" "}
            <span aria-hidden="true">→</span>
          </WaqLink>
        </div>

        <ul className="grid list-none gap-3">
          {model.details.map((detail, d) => (
            <li
              key={detail}
              className="rounded-card border border-ondark-border-soft bg-ondark-card px-[22px] py-5"
            >
              <span className="mb-2 block text-micro font-extrabold uppercase text-yellow">
                {String(d + 1).padStart(2, "0")}
              </span>
              <span className="block text-card-title font-bold">{detail}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
